<template>
  <div class="page fade-in">
    <!-- Header + Tabs -->
    <div class="header-row">
      <div class="tab-bar">
        <div
          v-for="t in tabList"
          :key="t.key"
          :class="['tab-pill', tab === t.key && 'active']"
          @click="tab = t.key"
        >
          <span>{{ t.label }}</span>
          <span v-if="t.count !== null" class="tab-count">{{ t.count }}</span>
        </div>
      </div>
      <button v-if="tab !== 'orders'" class="btn-modern" @click="openNew = true">
        <i class="fa fa-plus"></i> Add Lead
      </button>
    </div>

    <!-- Search + Filters -->
    <div class="filter-row">
      <input
        class="input search"
        v-model="q"
        placeholder="Search by name, phone, email, or code"
      />

      <template v-if="tab === 'leads'">
        <select class="input" v-model="statusFilter">
          <option value="">All Leads</option>
          <option value="follow_up">Active (Follow-up)</option>
          <option value="dead">Inactive (Dead)</option>
        </select>
      </template>

      <template v-else-if="tab === 'inquiries'">
        <div class="helper dim">Showing Inquiries (Lead converted)</div>
        <div class="helper dim" style="margin-top:4px;">
          Docs can be uploaded anytime. After conversion to Inquiry, required docs become overdue after 24 hours.
          (Sending a quotation is gated until required docs are uploaded.)
        </div>
      </template>

      <template v-else>
        <select class="input" v-model="stageFilter">
          <option value="">All Stages</option>
          <option v-for="s in stages" :key="s" :value="s">{{ s }}</option>
        </select>
        <button class="btn-small" @click="loadOrders">Refresh</button>
      </template>

      <!-- View Toggle (only for Leads/Inquiries) -->
      <div v-if="tab !== 'orders'" class="view-toggle">
        <button
          :class="['toggle-btn', viewMode==='cards' && 'active']"
          @click="viewMode='cards'"
          title="Card view"
        >
          ▦ Cards
        </button>
        <button
          :class="['toggle-btn', viewMode==='list' && 'active']"
          @click="viewMode='list'"
          title="List view"
        >
          ☰ List
        </button>
      </div>
    </div>

    <!-- Leads & Inquiries (Cards) -->
    <div v-if="tab !== 'orders' && viewMode==='cards'" class="cards-grid">
      <div v-for="c in visibleRows" :key="c._key" class="lead-card card-hover">
        <div
          class="corner-chip"
          :class="c.kind === 'inquiry' ? 'chip-inquiry' : 'chip-lead'"
        >
          {{ c.kind === 'inquiry' ? 'INQUIRY' : 'LEAD' }}
        </div>

        <div
          class="card-body"
          @click="$router.push({ name: 'sales-customer-detail', params: { id: c.leadId } })"
        >
          <h3 class="lead-name">{{ c.name }}</h3>
          <p class="lead-sub">
            <span v-if="c.phone">{{ c.phone }}</span>
            <span v-if="c.phone && c.email"> • </span>
            <span v-if="c.email">{{ c.email }}</span>
            <span v-if="c.address"> • {{ c.address }}</span>
          </p>

          <div class="tags">
            <span class="badge" :class="statusBadge(c)">
              {{ prettyStatus(c) }}
            </span>

            <span
              v-if="c.kind === 'lead' && c.status === 'follow_up' && c.followUpDate"
              class="badge"
              :class="urgencyClass(c)"
            >
              {{ urgencyText(c) }}
            </span>

            <span class="badge mono">#{{ c.leadCode || c.leadId }}</span>

            <span
              v-if="c.kind === 'inquiry'"
              class="badge"
              :class="docsBadgeClass(c.inquiryId)"
            >
              {{ docsBadgeText(c.inquiryId) }}
            </span>
          </div>

          <!-- Notes -->
          <p
            class="helper small"
            v-if="c.notes"
            style="color:#374151; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;"
            :title="c.notes"
          >
            {{ c.notes }}
          </p>

          <p
            class="helper small"
            v-if="c.kind === 'lead' && c.status === 'dead' && (c.deadReason || c.deadOther)"
          >
            Reason: {{ deadText(c) }}
          </p>

          <p class="helper small" v-if="c.kind === 'inquiry' && docsMeta(c.inquiryId).docsDueAt">
            Docs due: {{ formatDateTime(docsMeta(c.inquiryId).docsDueAt) }}
          </p>
        </div>

        <div class="card-footer">
          <router-link
            v-if="c.kind === 'inquiry'"
            class="btn-secondary"
            :to="toQuote(c.inquiryId, c.leadId)"
            @click.stop
          >
            Quotations
          </router-link>

          <button
            class="btn-secondary"
            v-if="c.kind === 'lead'"
            @click.stop="editLead(c)"
          >
            Edit
          </button>
        </div>
      </div>
    </div>

    <!-- Leads & Inquiries (List) -->
    <div v-else-if="tab !== 'orders' && viewMode==='list'" class="list-card">
      <div v-if="visibleRows && visibleRows.length" class="list">
        <div class="list-head">
          <div class="col col-name">Name & Contact</div>
          <div class="col col-tags">Status / Follow-up</div>
          <div class="col col-products">Products</div>
          <div class="col col-notes">Notes</div>
          <div class="col col-code">Code</div>
          <div class="col col-actions">Actions</div>
        </div>

        <div
          v-for="c in visibleRows"
          :key="c._key"
          class="list-row"
          @click="$router.push({ name: 'sales-customer-detail', params: { id: c.leadId } })"
        >
          <div class="col col-name">
            <div class="name-line">
              <span class="pill-type" :class="c.kind === 'inquiry' ? 'pill-inquiry' : 'pill-lead'">
                {{ c.kind === 'inquiry' ? 'INQUIRY' : 'LEAD' }}
              </span>
              <span class="lead-name">{{ c.name }}</span>
            </div>
            <div class="sub-line helper small">
              <span v-if="c.phone">{{ c.phone }}</span>
              <span v-if="c.phone && c.email"> • </span>
              <span v-if="c.email">{{ c.email }}</span>
              <span v-if="c.address"> • {{ c.address }}</span>
              <span
                v-if="c.kind === 'lead' && c.status === 'dead' && (c.deadReason || c.deadOther)"
              >
                • Reason: {{ deadText(c) }}
              </span>
            </div>
          </div>

          <div class="col col-tags">
            <span class="badge" :class="statusBadge(c)">
              {{ prettyStatus(c) }}
            </span>

            <span
              v-if="c.kind === 'lead' && c.status === 'follow_up' && c.followUpDate"
              class="badge"
              :class="urgencyClass(c)"
              style="margin-left:6px;"
            >
              {{ urgencyText(c) }}
            </span>

            <span
              v-if="c.kind === 'inquiry'"
              class="badge"
              :class="docsBadgeClass(c.inquiryId)"
              style="margin-left:6px;"
            >
              {{ docsBadgeText(c.inquiryId) }}
            </span>
          </div>

          <!-- kept column to preserve layout -->
          <div class="col col-products">
            <span class="badge light">—</span>
          </div>

          <div class="col col-notes">
            <span class="note-clip" :title="c.notes || '—'">{{ c.notes || '—' }}</span>
          </div>

          <div class="col col-code">
            <span class="badge mono">#{{ c.leadCode || c.leadId }}</span>
          </div>

          <div class="col col-actions" @click.stop>
            <router-link
              v-if="c.kind === 'inquiry'"
              class="btn-secondary"
              :to="toQuote(c.inquiryId, c.leadId)"
            >
              Quotations
            </router-link>

            <button
              class="btn-secondary"
              v-if="c.kind === 'lead'"
              @click="editLead(c)"
            >
              Edit
            </button>
          </div>
        </div>
      </div>
      <div v-else class="helper dim">No records to show.</div>
    </div>

    <!-- Orders -->
    <div v-else class="card table-card">
      <div v-if="ordersVisible.length">
        <div v-for="o in ordersVisible" :key="o.id" class="order-row">
          <div class="order-info">
            <div class="order-line">
              <span class="badge" :class="stageBadge(o.stage)">
                {{ prettyStage(o.stage) }}
              </span>
              <span class="mono helper">#{{ o.id }}</span>
              <span v-if="o.leadCode" class="mono helper">
                • {{ o.leadCode }}
              </span>
            </div>
            <div class="order-name">{{ o.leadName || ('Lead #' + o.leadId) }}</div>
            <div class="helper small">
              Placed on {{ formatDate(o.createdAt) }}
              <span v-if="o.quotationId"> • Quotation #{{ o.quotationId }}</span>
            </div>
          </div>
          <router-link
            class="btn-secondary"
            :to="{ name: 'sales-order-detail', params: { id: o.id } }"
          >
            Open
          </router-link>
        </div>
      </div>
      <div v-else class="helper dim">No orders found.</div>
    </div>

    <!-- ===================== Modals ===================== -->
    <!-- Add Lead -->
    <transition name="fadefast">
      <div
        v-if="openNew"
        class="modal-overlay"
        @click="closeAdd"
        aria-modal="true"
        role="dialog"
      >
        <div class="modal" @click.stop>
          <div class="modal-header">
            <h3>Add Lead</h3>
            <button class="icon-btn" @click="closeAdd" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            <customer-form @close="closeAdd" @saved="afterCreate" />
          </div>
        </div>
      </div>
    </transition>

    <!-- Edit Lead -->
    <transition name="fadefast">
      <div
        v-if="editing"
        class="modal-overlay"
        @click="closeEdit"
        aria-modal="true"
        role="dialog"
      >
        <div class="modal" @click.stop>
          <div class="modal-header">
            <h3>Edit Lead</h3>
            <button class="icon-btn" @click="closeEdit" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            <customer-form
              :value="editing"
              @close="closeEdit"
              @saved="afterEdit"
            />
          </div>
        </div>
      </div>
    </transition>
    <!-- ================================================== -->
  </div>
</template>

<script>
import CustomerForm from "@/components/customers/CustomerForm.vue";

function daysBetween(a, b) {
  const d1 = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const d2 = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((d2 - d1) / 86400000);
}

function runPool(items, worker, concurrency) {
  const list = Array.isArray(items) ? items.slice() : [];
  const results = [];
  let i = 0;

  const runners = new Array(Math.min(concurrency, list.length)).fill(0).map(async () => {
    while (i < list.length) {
      const idx = i++;
      try {
        results[idx] = await worker(list[idx], idx);
      } catch (e) {
        results[idx] = null;
      }
    }
  });

  return Promise.all(runners).then(() => results);
}

export default {
  components: { CustomerForm },

  data() {
    return {
      tab: "leads",
      q: "",
      statusFilter: "",
      stageFilter: "",
      openNew: false,
      editing: null,

      // keep separate to match app.py resources
      leads: [],
      inquiries: [],
      orders: [],

      // inquiry docs summary map: { [inquiryId]: { docsOk, docsOverdue, docsState, docsDueAt, ... } }
      docsMap: {},

      viewMode: "cards",

      // FINAL SALES-VISIBLE ORDER STAGES
      stages: [
        "BM_PENDING",
        "BM_REJECTED",
        "BM_APPROVED",
        "DISPATCH_PENDING",
        "INSTALL_PENDING",
        "CLOSED",
      ],
    };
  },

  computed: {
    leadsFiltered() {
      const q = this.q.trim().toLowerCase();
      const status = this.statusFilter;

      return (this.leads || [])
        .filter(l => l.status !== "converted_to_inquiry") // ✅ hide converted leads
        .filter(l => {
          const blob = `${l.name || ""} ${l.phone || ""} ${l.email || ""} ${l.leadCode || ""}`.toLowerCase();
          const statusOk = !status || l.status === status;
          const qOk = !q || blob.includes(q);
          return statusOk && qOk;
        });
    },

    orderInquiryIds() {
      const set = new Set();
      (this.orders || []).forEach(o => {
        if (o.inquiryId) set.add(String(o.inquiryId));
      });
      return set;
    },



    inquiriesFiltered() {
      const q = this.q.trim().toLowerCase();
      const orderIids = this.orderInquiryIds;

      return (this.inquiries || [])
        .filter(i => !orderIids.has(String(i.id))) // ✅ hide inquiries that became orders
        .filter(i => {
          const blob = `${i.customerName || ""} ${i.customerPhone || ""} ${i.leadCode || ""} ${i.id || ""}`.toLowerCase();
          return !q || blob.includes(q);
        });
    },


    // Normalize into the old UI shape without touching styles
    visibleRows() {
      if (this.tab === "leads") {
        return (this.leadsFiltered || []).map(l => ({
          _key: `L-${l.id}`,
          kind: "lead",
          leadId: l.id,
          inquiryId: null,
          name: l.name,
          phone: l.phone,
          email: l.email,
          address: l.address,
          leadCode: l.leadCode,
          status: l.status, // follow_up | dead | converted_to_inquiry
          followUpDate: l.followUpDate,
          notes: l.notes,
          deadReason: l.deadReason,
          deadOther: l.deadOther,
          products: l.products,
          userId: l.userId,
          branchId: l.branchId,
        }));
      }
      if (this.tab === "inquiries") {
        return (this.inquiriesFiltered || []).map(i => ({
          _key: `I-${i.id}`,
          kind: "inquiry",
          leadId: i.leadId,           // important: detail page can still open via leadId
          inquiryId: i.id,            // used for docs badge
          name: i.customerName,
          phone: i.customerPhone,
          email: null,
          address: null,
          leadCode: i.leadCode,
          status: "inquiry",
          followUpDate: null,
          notes: null,
          deadReason: null,
          deadOther: null,
          products: null,
          userId: i.ownerUserId,
          branchId: i.branchId,
        }));
      }
      return [];
    },

    ordersVisible() {
      const q = this.q.trim().toLowerCase();

      let list = (this.orders || []).map(o => ({
        ...o,
        leadName: o.leadName || o.customerName || "",
        leadCode: o.leadCode || "",
      }));

      // ✅ default: hide CLOSED unless explicitly filtering
      if (!this.stageFilter) {
        list = list.filter(o => o.stage !== "CLOSED");
      }

      return q
        ? list.filter(o => `${o.leadName} ${o.leadCode} ${o.id}`.toLowerCase().includes(q))
        : list;
    },


    tabList() {
      return [
        {
          key: "leads",
          label: "Leads",
          count: (this.leads || []).filter(r => r.status !== "converted_to_inquiry").length,
        },
        {
          key: "inquiries",
          label: "Inquiries",
          count: (this.inquiriesFiltered || []).length,
        },
        {
          key: "orders",
          label: "Orders",
          count: (this.orders || []).filter(o => o.stage !== "CLOSED").length,
        },
      ];
    },

    anyModalOpen() {
      return !!(this.openNew || this.editing);
    },
  },

  created() {
    try {
      const v = localStorage.getItem("edda_leads_view");
      if (v === "list" || v === "cards") this.viewMode = v;
    } catch (_) {}
    this.loadAll();
  },

  watch: {
    tab(n) {
      if (n === "orders" && !this.orders.length) this.loadOrders();
      if (n === "leads" && !this.leads.length) this.loadLeads();
      if (n === "inquiries" && !this.inquiries.length) this.loadInquiries();
      if (n === "inquiries") this.loadDocsStatusBulk();
    },
    stageFilter() {
      if (this.tab === "orders") this.loadOrders();
    },
    statusFilter() {
      if (this.tab === "leads") this.loadLeads(); // server supports status filter
    },
    anyModalOpen(n) {
      document.body.classList.toggle("no-scroll", n);
    },
    viewMode(n) {
      try {
        localStorage.setItem("edda_leads_view", n);
      } catch (_) {}
    },
  },

  methods: {
    // ---------------- DATA LOAD (MATCHES app.py) ----------------
    async loadAll() {
      await this.loadLeads();
      await this.loadInquiries();
      await this.loadDocsStatusBulk();
      await this.loadOrders();    
      if (this.tab === "orders") await this.loadOrders();
    },

    async loadLeads() {
      // GET /api/leads?status=follow_up|dead|all&q=...
      const params = {};
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.q && this.q.trim()) params.q = this.q.trim();

      const { data } = await this.$api.get("/leads", { params });

      // normalize keys from backend (it already returns leadCode, followUpDate etc)
      this.leads = Array.isArray(data) ? data : [];
    },

    async loadInquiries() {
      // GET /api/inquiries?status=DOCS_PENDING|DOCS_COMPLETE&overdue=1
      // (UI doesn’t expose these filters yet, so we fetch default list)
      const { data } = await this.$api.get("/inquiries");
      this.inquiries = Array.isArray(data) ? data : [];
    },

    async loadOrders() {
      const { data } = await this.$api.get("/orders", {
        params: this.stageFilter ? { stage: this.stageFilter } : {},
      });
      this.orders = Array.isArray(data) ? data : [];
    },

    // ---------------- DOCS (MATCHES app.py: /inquiries/<id> returns docs summary) ----------------
    async loadDocsStatusBulk() {
      const inquiryIds = (this.inquiries || []).map(x => x.id).filter(Boolean);

      if (!inquiryIds.length) {
        this.docsMap = {};
        return;
      }

      const out = {};
      await runPool(
        inquiryIds,
        async (iid) => {
          const { data } = await this.$api.get(`/inquiries/${iid}`);
          // app.py returns { inquiry: {...}, docs: {...} }
          if (data && data.docs) out[iid] = data.docs;
          return true;
        },
        6
      );

      this.docsMap = out;
    },

    docsMeta(inquiryId) {
      return (this.docsMap && inquiryId && this.docsMap[inquiryId]) ? this.docsMap[inquiryId] : {};
    },

    docsBadgeText(inquiryId) {
      const m = this.docsMeta(inquiryId);
      const state = (m.docsState || "").toUpperCase();
      if (state === "COMPLETE") return "Docs complete";
      if (state === "OVERDUE") return "Docs overdue";
      return "Docs pending";
    },

    docsBadgeClass(inquiryId) {
      const m = this.docsMeta(inquiryId);
      if (m.docsOverdue) return "badge-red";
      if (m.docsOk) return "badge-green";
      return "badge-amber";
    },

    // ---------------- UI HELPERS ----------------
    prettyStatus(c) {
      if (!c) return "";
      if (c.kind === "inquiry") return "Inquiry";
      if (c.status === "dead") return "Dead";
      return "Follow-up";
    },

    statusBadge(c) {
      if (!c) return "badge-blue";
      if (c.kind === "inquiry") return "badge-green";
      if (c.status === "dead") return "badge-red";
      return "badge-blue";
    },

    prettyStage(s) {
      return (s || "").replace(/_/g, " ");
    },

    stageBadge(s) {
      return {
        BM_PENDING: "badge-amber",
        BM_REJECTED: "badge-red",
        BM_APPROVED: "badge-blue",
        DISPATCH_PENDING: "badge-blue",
        INSTALL_PENDING: "badge-blue",
        CLOSED: "badge-green",
      }[s] || "badge-blue";
    },

    urgencyText(c) {
      if (!c || c.kind !== "lead" || c.status !== "follow_up" || !c.followUpDate) return "";
      const diff = daysBetween(new Date(), new Date(c.followUpDate));
      if (diff < 0) return `${Math.abs(diff)} day(s) overdue`;
      if (diff === 0) return "Due today";
      if (diff <= 3) return `Due in ${diff} day(s)`;
      return `Due in ${diff} days`;
    },

    urgencyClass(c) {
      const t = this.urgencyText(c);
      if (t.includes("overdue")) return "badge-red";
      if (t.includes("today")) return "badge-amber";
      if (t.match(/in [1-3]/)) return "badge-yellow";
      return "badge-green";
    },

    deadText(c) {
      if (!c) return "";
      const r = (c.deadReason || "").toString();
      if (r === "other") return (c.deadOther || "Other").toString();
      return r || (c.deadOther || "");
    },

    formatDate(s) {
      if (!s) return "—";
      const d = new Date(s);
      if (isNaN(d.getTime())) return String(s);
      return d.toLocaleDateString();
    },

    formatDateTime(s) {
      if (!s) return "—";
      const d = new Date(s);
      if (isNaN(d.getTime())) return String(s);
      return d.toLocaleString();
    },

    // ---------------- MODALS ----------------
    closeAdd() {
      this.openNew = false;
    },

    closeEdit() {
      this.editing = null;
    },

    // ---------------- CRUD ----------------
    afterCreate() {
      this.openNew = false;
      this.loadAll();
    },

    editLead(c) {
      // Only leads are editable here
      if (!c || c.kind !== "lead") return;
      this.editing = {
        // CustomerForm expects the lead shape from backend
        id: c.leadId,
        name: c.name,
        phone: c.phone,
        email: c.email,
        address: c.address,
        status: c.status,
        followUpDate: c.followUpDate,
        deadReason: c.deadReason,
        deadOther: c.deadOther,
        products: c.products,
        notes: c.notes,
      };
    },

    afterEdit() {
      this.editing = null;
      this.loadAll();
    },

    toQuote(inquiryId, leadId){
      return {
        name: 'sales-quotations',
        query: {
          inquiryId: String(inquiryId),
          ...(leadId ? { leadId: String(leadId) } : {})
        }
      }
    },

  },
};
</script>

<style scoped>
/* (styles unchanged) */
.fade-in { animation: fade 0.3s ease-in; }
@keyframes fade { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.filter-row { display: flex; gap: 10px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
.view-toggle { margin-left: auto; display: inline-flex; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
.toggle-btn { appearance: none; background: transparent; border: none; padding: 8px 12px; font-weight: 600; cursor: pointer; color: #374151; }
.toggle-btn + .toggle-btn { border-left: 1px solid #e5e7eb; }
.toggle-btn:hover { background: #f9fafb; }
.toggle-btn.active { background: #db1212; color: #fff; }
.tab-bar { display: flex; gap: 10px; }
.tab-pill { background: #fff; border: 1px solid #e5e7eb; border-radius: 999px; padding: 8px 14px; font-weight: 600; display: flex; align-items: center; gap: 6px; transition: all 0.25s ease; cursor: pointer; }
.tab-pill:hover { border-color: #db1212; color: #db1212; }
.tab-pill.active { background: #db1212; color: #fff; border-color: #db1212; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
.tab-count { background: rgba(0,0,0,0.08); border-radius: 10px; padding: 2px 8px; font-size: 12px; }
.tab-pill.active .tab-count { background: rgba(255,255,255,0.25); }
.btn-modern { background: #db1212; color: #fff; border: none; border-radius: 8px; padding: 8px 14px; font-weight: 600; transition: 0.2s; }
.btn-modern:hover { background: #b20f0f; }
.btn-small { background: #db1212; color: #fff; border: none; border-radius: 6px; padding: 6px 10px; font-size: 13px; }
.btn-secondary { background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 10px; transition: 0.2s; cursor: pointer; }
.btn-secondary:hover { background: #e5e7eb; }
.lead-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px; position: relative; display: flex; flex-direction: column; justify-content: space-between; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.card-hover:hover { transform: translateY(-2px); transition: 0.2s; box-shadow: 0 4px 8px rgba(0,0,0,0.08); }
.corner-chip { position: absolute; top: 10px; right: 10px; font-size: 11px; padding: 3px 8px; border-radius: 999px; font-weight: 700; }
.chip-lead { background: #e0e7ff; color: #1d4ed8; }
.chip-inquiry { background: #dcfce7; color: #166534; }
.lead-name { font-weight: 700; margin-bottom: 4px; }
.lead-sub { color: #6b7280; font-size: 13px; margin-bottom: 8px; }
.tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.badge { border-radius: 6px; padding: 3px 8px; font-size: 11px; font-weight: 600; display: inline-flex; align-items: center; white-space: nowrap; }
.badge-green { background: #16a34a; color: #fff; }
.badge-blue { background: #1d4ed8; color: #fff; }
.badge-amber { background: #f59e0b; color: #111; }
.badge-yellow { background: #facc15; color: #111; }
.badge-red { background: #dc2626; color: #fff; }
.badge.light { background: #f3f4f6; color: #111; }
.badge.mono { font-family: monospace; background: #e5e7eb; color: #111; }
.table-card { background: #fff; border-radius: 8px; padding: 10px 16px; }
.order-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-top: 1px solid #f3f4f6; }
.order-row:first-child { border-top: none; }
.order-info { flex: 1; }
.order-line { display: flex; align-items: center; gap: 6px; }
.order-name { font-weight: 600; margin-top: 4px; }
.list-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
.list { width: 100%; display: block; }
.list-head, .list-row { display: grid; grid-template-columns: 2.2fr 1.3fr 1.2fr 1.2fr .8fr .8fr; gap: 12px; align-items: center; }
.list-head { background: #f9fafb; border-bottom: 1px solid #eef2f7; padding: 10px 14px; font-weight: 700; color: #374151; }
.list-row { padding: 12px 14px; border-top: 1px solid #f3f4f6; cursor: pointer; }
.list-row:hover { background: #fcfcfd; }
.col { min-width: 0; }
.col-name .name-line { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pill-type { border-radius: 999px; font-size: 10px; font-weight: 800; padding: 2px 8px; }
.pill-lead { background: #e0e7ff; color: #1d4ed8; }
.pill-inquiry { background: #dcfce7; color: #166534; }
.sub-line { color: #6b7280; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.col-products .badge.light { max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
.col-notes .note-clip { display: block; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #374151; }
.col-actions { display: flex; justify-content: flex-start; }
.fadefast-enter-active, .fadefast-leave-active { transition: opacity .15s ease; }
.fadefast-enter, .fadefast-leave-to { opacity: 0; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
.modal { background: #fff; width: 100%; max-width: 720px; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,.15); display: flex; flex-direction: column; max-height: 90vh; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #f1f5f9; }
.modal-header h3 { margin: 0; font-size: 18px; font-weight: 700; }
.icon-btn { background: transparent; border: none; font-size: 22px; line-height: 1; cursor: pointer; padding: 4px 8px; border-radius: 8px; }
.icon-btn:hover { background: #f3f4f6; }
.modal-body { padding: 12px 16px; overflow: auto; }
.helper.dim { color: #6b7280; }
.helper.small { font-size: 12px; }
.card-footer{
  display:flex;
  gap:8px;
  justify-content:flex-end;
  margin-top:10px;
}
.mono { font-family: monospace; }
:global(body.no-scroll) { overflow: hidden; }
@media (max-width: 800px) { .list-head, .list-row { grid-template-columns: 1.8fr 1fr 1fr 1fr .7fr .8fr; } }
@media (max-width: 640px) { .list-head { display: none; } .list-row { grid-template-columns: 1fr; align-items: flex-start; } .col-tags, .col-products, .col-notes, .col-code, .col-actions { margin-top: 6px; } .col-actions { justify-content: flex-start; } }
</style>
